import { differenceInDays } from 'date-fns';

import { PlanningDataFragment } from '@spotify/confidence-graphql';

export type PlanningDates = {
  plannedStartDate: Date | null;
  plannedEndDate: Date | null;
  remainingDays: number | null;
  plannedRuntime: number | null;
  actualStartDate: Date | null;
  actualEndDate: Date | null;
  currentRuntime: number;
  actualRuntime: number | null;
  progress: number | null;
  isRuntimeUnknown: boolean;
};

export const calculateProgressFromPlan = (
  currentRuntime: number,
  currentRemainingRuntime: number | null,
): number | null => {
  if (currentRemainingRuntime === null) return null;
  if (currentRemainingRuntime === 0) return 100;
  return Math.min(
    Math.max(0, Math.round((currentRuntime / currentRemainingRuntime) * 100)),
    100,
  );
};

const getRuntimeSeconds = (
  startTime?: string | null,
  endTime?: string | null,
): number | null => {
  if (!startTime || !endTime) return null;

  const runtimeSeconds =
    (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000;

  return Number.isFinite(runtimeSeconds) && runtimeSeconds >= 0
    ? Math.round(runtimeSeconds)
    : null;
};

export const getExperimentRuntimeSeconds = (
  planning?: Pick<PlanningDataFragment, 'planned' | 'actual'> | null,
): number | null => {
  const actualRuntimeSeconds = getRuntimeSeconds(
    planning?.actual?.startTime,
    planning?.actual?.endTime,
  );
  if (actualRuntimeSeconds !== null) return actualRuntimeSeconds;

  const actualToPlannedRuntimeSeconds = getRuntimeSeconds(
    planning?.actual?.startTime,
    planning?.planned?.endTime,
  );
  if (actualToPlannedRuntimeSeconds !== null) {
    return actualToPlannedRuntimeSeconds;
  }

  return getRuntimeSeconds(
    planning?.planned?.startTime,
    planning?.planned?.endTime,
  );
};

const SECONDS_PER_DAY = 86400;

export const snapRuntimeToDays = (runtimeSeconds: number): number =>
  Math.ceil(runtimeSeconds / SECONDS_PER_DAY) * SECONDS_PER_DAY;

export function usePlanningDates(
  planning?: Pick<PlanningDataFragment, 'planned' | 'actual'> | null,
): PlanningDates {
  const today = new Date();

  const plannedStartDate = planning?.planned?.startTime
    ? new Date(planning?.planned?.startTime)
    : null;
  const plannedEndDate = planning?.planned?.endTime
    ? new Date(planning?.planned?.endTime)
    : null;
  const remainingDays = plannedEndDate
    ? differenceInDays(plannedEndDate, today)
    : null;

  const plannedRuntime =
    plannedStartDate && plannedEndDate
      ? differenceInDays(plannedEndDate, plannedStartDate) + 1
      : null;

  const actualStartDate = planning?.actual?.startTime
    ? new Date(planning?.actual?.startTime)
    : null;
  const actualEndDate = planning?.actual?.endTime
    ? new Date(planning?.actual?.endTime)
    : null;

  const currentRuntime = actualStartDate
    ? differenceInDays(today, actualStartDate)
    : 0;

  const actualRuntime =
    actualStartDate && actualEndDate
      ? differenceInDays(actualEndDate, actualStartDate) + 1
      : null;

  const currentRemainingRuntime =
    actualStartDate && plannedEndDate
      ? differenceInDays(plannedEndDate, actualStartDate)
      : null;

  const progress = calculateProgressFromPlan(
    currentRuntime,
    currentRemainingRuntime,
  );

  // If the experiment is estimated or not.
  const isRuntimeUnknown = !Boolean(plannedEndDate);

  return {
    plannedStartDate,
    plannedEndDate,
    remainingDays,
    plannedRuntime,
    actualStartDate,
    actualEndDate,
    currentRuntime,
    actualRuntime,
    progress,
    isRuntimeUnknown,
  };
}
